import {
	WhatsApp as WhatsappIcon
} from "@material-ui/icons"
import React, { ReactNode, useRef, useState } from "react"

import CustomFieldsDialog from "@/pages/Admin/CustomFields"
import InstanceSelectorDialogue from "@/components/InstanceSelectorDialogue"
import AuthService from "@/services/Auth"
import { deviceIsMobile } from "@/utils/checkDevice"

import {
	ShieldCheckIcon as UserRoleIcon,
	GraduationCapIcon as AcademyIcon,
	HeadsetIcon as AttendantIcon,
	WorkflowIcon as AutomationIcon,
	Kanban as BoardIcon,
	Calculator as CalculatorIcon,
	ChartPieIcon,
	BotMessageSquareIcon as ChatBotIcon,
	CircleUserIcon,
	Contact as ClientCatalogIcon,
	TagsIcon as ConversionEventsIcon,
	TrendingUpIcon as ConversionIcon,
	CreditCardIcon,
	Sprout as EcosystemIcon,
	GiftIcon,
	UsersIcon as GroupIcon,
	UsersRound as UserRoundGroupIcon,
	HeartHandshakeIcon as HandshakeIcon,
	CircleQuestionMark as HelpIcon,
	HouseIcon,
	BookCopy as InstanceIcon,
	GitForkIcon as IntegrationIcon,
	LayoutGrid as WidgetsIcon,
	LogOutIcon,
	MessageCircle as MessageCircleIcon,
	MessagesSquareIcon,
	UnplugIcon as PluginIcon,
	MessageSquareShareIcon as QuickReplyIcon,
	RouteIcon,
	ShieldCheckIcon as SecurityIcon,
	SendHorizonalIcon,
	HandPlatter as ServicesIcon,
	ListPlus as ListPlusIcon,
	SparklesIcon,
	TagIcon,
	Book as TemplateGeneratorIcon,
	BookTextIcon as TemplateIcon,
	WrenchIcon as ToolIcon,
	GlobeIcon as UTMReportIcon,
	PhoneIcon as VoiceCallReportIcon,
	PresentationIcon as MeetingIcon,
	WandSparklesIcon,
	WebhookIcon
} from "lucide-react"

import {
	ActiveCampaignSVGIcon,
	GoogleDriveSVGIcon,
	MetaLogo,
	RDStationSVGIcon,
	SuperlogicaSVGIcon
} from "@/assets/icons"

import { SvgIcon, WABAMigrationModal } from "@/components"
import { FeatureType } from "@/components/ListItemMenu"
import { adminConsoleAppUrl, playgroundIAUrl } from "@/config/url"
import useSubscriptionLimits from "@/hooks/useSubscriptionLimits"
import { usePermissionStateStore } from "@/store/PermissionState"
import { PERMISSION_DENIED_MESSAGE } from "@/components/PermissionGate/BasePermissionGate"
import HardCoded from "@/services/HardCoded"
import { useGlobalStateStore } from "@/store/GlobalState"
import { externalLinks, letalkLinks } from "@/utils/link"
import ReferralPopupTrigger from "@/pages/Attendance/ReferralPopup/ReferralPopupTrigger"
import {
	ROUTE_ACCOUNT,
	ROUTE_AI,
	ROUTE_ATTENDANCE,
	ROUTE_AUTOMATION,
	ROUTE_CONTACTS,
	ROUTE_CONVERSION,
	ROUTE_ECOSYSTEM,
	ROUTE_HOME,
	ROUTE_PLUGINS,
	ROUTE_REPORT
} from "@/utils/menuRoutesUtils"

export type MenuItem = {
	label: string
	icon: ReactNode
	to?: string
	isExternal?: boolean
	featureType?: FeatureType
	isDisabled?: boolean
	disabledTooltip?: string
	onClick?: () => void
	isHighlighted?: boolean
}

export type MenuCategory = {
	id: string
	label: string
	icon: ReactNode
	to?: string
	items?: MenuItem[]
	placement?: "right-end" | "bottom-start" | "bottom-end" | "left-start" | "left-end" | "right-start" | "top-start" | "top-end"
	isDisabled?: boolean
	isExternal?: boolean
	hasBackground?: boolean
	hasPopper?: boolean
	mainRoute?: string
	isSecondary?: boolean
}

export type MenuType = "whatsapp" | "waba"

const PLANS_ID_CAN_USE_AI_PLAYGROUND = [
	22, // Parceiro Letalk
	28, // Tudo Liberado sem Grupos (apenas time Letalk)
	34, // Tudo Liberado sem Grupos (apenas time Letalk) - WABA
	35 // Parceiro Letalk - WABA
]

export type useDashboardMenuDataReturnType = {
	defaultMenuCategories: MenuCategory[]
	bottomMenuCategories: MenuCategory[]
	hoveredCategory: string | null
	setHoveredCategory: (id: string | null) => void
	anchorRefs: React.MutableRefObject<Record<string, HTMLElement | null>>
	handleEnter: (id: string) => void
	handleLeave: () => void
	handleCategoryClick: (id: string) => void
	isInstanceOwner: boolean
	canUseTrackableLinks: boolean
	canUseConversionEvents: boolean
}

const createIcon = (Component: React.ElementType, className: string): ReactNode => {
	return React.createElement(Component, { className, size: 20 })
}

export const useDashboardMenuData = (
	integrationType: MenuType = "whatsapp",
	classes: Record<string, string>,
	hiddenTabs?: boolean
): useDashboardMenuDataReturnType => {
	const globalStateStore = useGlobalStateStore()
	const isInstanceOwner = globalStateStore.user.is_instance_owner
	const isSmallScreen = false

	const { checkPermission } = usePermissionStateStore()

	const trackableLinksPermission = useSubscriptionLimits("permission", "trackable_links")
	const trackableLinksPermissionTemporary = useSubscriptionLimits("permission", "trackable_links_temporary")
	const chatbotBlockConversionEventsPermission = useSubscriptionLimits("permission", "chatbot_block_conversion_events")
	const chatbotBlockConversionEventsPermissionTemporary = useSubscriptionLimits("permission", "chatbot_block_conversion_events_temporary")
	const aiCreditsPermissionLimit = useSubscriptionLimits("permission", "ai_resources")
	const meetingAnalyzerPermission = useSubscriptionLimits("permission", "meeting_analyzer")

	const canUseOutboundWebhooks = HardCoded.checkFeatureFlag("canUseOutboundWebhooks")

	const canUseSuperlogicaBlock = HardCoded.checkFeatureFlag("canUseSuperlogicaBlock")

	const hasPermissionToUseTrackableLinks = !trackableLinksPermission.mustBlock || !trackableLinksPermissionTemporary.mustBlock
	const canUseTrackableLinks = hasPermissionToUseTrackableLinks

	const hasPermissionToUseConversionEvents = !chatbotBlockConversionEventsPermission.mustBlock || !chatbotBlockConversionEventsPermissionTemporary.mustBlock
	const canUseConversionEvents = hasPermissionToUseConversionEvents

	const planIdInstance = globalStateStore.instance.subscriptionData.plan_id
	const hasPermissionToUseAIPlayground = HardCoded.checkFeatureFlag("playgroundAIBetaTesters") || PLANS_ID_CAN_USE_AI_PLAYGROUND.includes(planIdInstance) || !aiCreditsPermissionLimit.mustBlock

	const [hoveredCategory, setHoveredCategory] = useState<string | null>(null)
	const anchorRefs = useRef<Record<string, HTMLElement | null>>({})

	const handleOpenMultipleInstancesDialogue = () => {
		InstanceSelectorDialogue.open({
			actualUserInstanceId: globalStateStore.instance.instance_id,
			allUserInstances: globalStateStore.user.user_instances,
			isAdminView: globalStateStore?.user?.extra_data?.is_admin_mode
		})
	}

	const handleLogout = async () => {
		await AuthService.logout("/signin")
	}

	const icons = {
		home: createIcon(HouseIcon, classes.icon),
		sparkles: createIcon(SparklesIcon, classes.icon),
		magicWand: createIcon(WandSparklesIcon, classes.icon),
		group: createIcon(GroupIcon, classes.icon),
		messageBlast: createIcon(SendHorizonalIcon, classes.icon),
		whatsapp: createIcon(WhatsappIcon, classes.icon),
		template: createIcon(TemplateIcon, classes.icon),
		calculator: createIcon(CalculatorIcon, classes.icon),
		help: createIcon(HelpIcon, classes.helpIcon),
		automation: createIcon(AutomationIcon, classes.icon),
		integration: createIcon(IntegrationIcon, classes.icon),
		conversion: createIcon(ConversionIcon, classes.icon),
		conversionEvents: createIcon(ConversionEventsIcon, classes.icon),
		board: createIcon(BoardIcon, classes.icon),
		route: createIcon(RouteIcon, classes.icon),
		attendance: createIcon(MessagesSquareIcon, classes.icon),
		goToInbox: createIcon(MessageCircleIcon, classes.icon),
		clientCatalog: createIcon(ClientCatalogIcon, classes.icon),
		tag: createIcon(TagIcon, classes.icon),
		quickReply: createIcon(QuickReplyIcon, classes.icon),
		attendants: createIcon(AttendantIcon, classes.icon),
		team: createIcon(GroupIcon, classes.icon),
		report: createIcon(ChartPieIcon, classes.icon),
		tagReport: createIcon(TagIcon, classes.icon),
		utmReport: createIcon(UTMReportIcon, classes.icon),
		voiceCallReport: createIcon(VoiceCallReportIcon, classes.icon),
		plugin: createIcon(PluginIcon, classes.icon),
		ecosystem: createIcon(EcosystemIcon, classes.icon),
		handshake: createIcon(HandshakeIcon, classes.icon),
		gift: createIcon(GiftIcon, classes.icon),
		tool: createIcon(ToolIcon, classes.icon),
		academy: createIcon(AcademyIcon, classes.icon),
		myData: createIcon(CircleUserIcon, classes.icon),
		security: createIcon(SecurityIcon, classes.icon),
		userRole: createIcon(UserRoleIcon, classes.icon),
		creditCard: createIcon(CreditCardIcon, classes.icon),
		chatBot: createIcon(ChatBotIcon, classes.icon),
		logout: createIcon(LogOutIcon, classes.icon),
		instance: createIcon(InstanceIcon, classes.icon),
		service: createIcon(ServicesIcon, classes.icon),
		activeCampaign: React.createElement(SvgIcon, { icon: ActiveCampaignSVGIcon, className: classes.icon }),
		rdStation: React.createElement(SvgIcon, { icon: RDStationSVGIcon, className: classes.icon }),
		googleDrive: React.createElement(SvgIcon, { icon: GoogleDriveSVGIcon, className: classes.icon, autoSize: true }),
		superlogica: React.createElement(SvgIcon, { icon: SuperlogicaSVGIcon, className: classes.icon, autoSize: true }),
		meta: React.createElement(SvgIcon, { icon: MetaLogo, className: classes.icon, autoSize: true }),
		templateGenerator: createIcon(TemplateGeneratorIcon, classes.icon),
		customFields: createIcon(ListPlusIcon, classes.icon),
		meeting: createIcon(MeetingIcon, classes.icon),
		webhook: createIcon(WebhookIcon, classes.icon),
		userRoundGroup: createIcon(UserRoundGroupIcon, classes.icon),
		widgets: createIcon(WidgetsIcon, classes.icon)
	}

	const canAccessAdminConsole = checkPermission("admin-console", "access")

	const canShowGroupsMenuItem =
		integrationType === "whatsapp" &&
		!!globalStateStore.instance.subscriptionData?.limits?.permission?.group

	const canShowWidgetsMenuItem = !!globalStateStore.instance.subscriptionData?.limits?.permission?.lechat

	const defaultMenuCategories: MenuCategory[] = [
		{
			id: "home",
			label: "Home",
			icon: icons.home,
			to: ROUTE_HOME,
			hasPopper: false,
			mainRoute: ROUTE_HOME
		},
		{
			id: "attendance",
			label: "Atendimento",
			icon: icons.attendance,
			mainRoute: ROUTE_ATTENDANCE,
			items: [
				{
					label: "Conversas (Inbox)",
					icon: icons.goToInbox,
					to: "/attendance",
					isExternal: !deviceIsMobile()
				},
				{
					label: "Atendentes",
					icon: icons.attendants,
					to: `${ROUTE_ATTENDANCE}/attendant`
				},
				{
					label: "Equipes",
					icon: icons.team,
					to: `${ROUTE_ATTENDANCE}/teams`
				},
				{
					label: "Cargos",
					icon: icons.userRole,
					to: `${ROUTE_ATTENDANCE}/roles`,
					featureType: "new" as FeatureType
				},
				...(integrationType === "whatsapp"
					? [
						{
							label: "Templates (API Oficial)",
							icon: icons.template,
							onClick: () => WABAMigrationModal.open({
								initialName: globalStateStore.user.name ?? "",
								initialPhone: globalStateStore.instance.responsible_phone_number ?? ""
							})
						}
					]
					: integrationType === "waba"
						? [
							{
								label: "Templates",
								icon: icons.template,
								to: `${ROUTE_ATTENDANCE}/templates`
							}
						]
						: []),
				{
					label: "Gerar templates com IA",
					icon: icons.templateGenerator,
					to: externalLinks.generatorTemplates,
					isExternal: true,
					featureType: "new" as FeatureType
				},
				{
					label: "Respostas rápidas",
					icon: icons.quickReply,
					to: `${ROUTE_ATTENDANCE}/quick-reply`
				},
				...(canShowWidgetsMenuItem ? [{
					label: "Widgets",
					icon: icons.widgets,
					to: `${ROUTE_ATTENDANCE}/widgets`
				}] : [])
			]
		},
		{
			id: "contacts",
			label: "Contatos",
			icon: icons.clientCatalog,
			mainRoute: ROUTE_CONTACTS,
			items: [
				{
					label: "Lista de contatos",
					icon: icons.clientCatalog,
					to: `${ROUTE_CONTACTS}/client-catalog`
				},
				...(canShowGroupsMenuItem ? [{
					label: "Lista de grupos",
					icon: icons.userRoundGroup,
					to: "/admin/groups"
				}] : []),
				{
					label: "Tags",
					icon: icons.tag,
					to: `${ROUTE_CONTACTS}/tags`
				},
				{
					label: "Importar contatos",
					icon: icons.group,
					to: `${ROUTE_CONTACTS}/import-contacts`
				},
				{
					label: "Campos Personalizados",
					icon: icons.customFields,
					onClick: () => CustomFieldsDialog.open()
				}
			]
		},
		{
			id: "automation",
			label: "Automações",
			icon: icons.automation,
			mainRoute: ROUTE_AUTOMATION,
			items: [
				{
					label: "Bots",
					icon: icons.chatBot,
					to: `${ROUTE_AUTOMATION}/flow`
				},
				...(canUseOutboundWebhooks ? [
					{
						label: "Webhook",
						icon: icons.webhook,
						to: `${ROUTE_AUTOMATION}/webhook`
					}
				] : []),
				...(integrationType === "whatsapp"
					? [
						{
							label: "Integrações",
							icon: icons.integration,
							to: `${ROUTE_AUTOMATION}/integration`
						}
					]
					: []),
				{
					label: "Envio em massa",
					icon: icons.messageBlast,
					to: `${ROUTE_AUTOMATION}/message-blast`
				}
			]
		},
		{
			id: "convertion",
			label: "Conversão",
			icon: icons.conversion,
			mainRoute: ROUTE_CONVERSION,
			items: [
				{
					label: "Funil",
					icon: icons.board,
					to: `${ROUTE_CONVERSION}/funnel`
				},
				{
					label: "Links Rastreáveis",
					icon: icons.route,
					to: canUseTrackableLinks
						? `${ROUTE_CONVERSION}/tools/trackable-links`
						: `${ROUTE_CONVERSION}/trackable-links/smart-conversions`
				},
				{
					label: "Eventos de Conversão",
					icon: icons.conversionEvents,
					to: canUseConversionEvents
						? "/admin/plugins/meta-ads"
						: `${ROUTE_CONVERSION}/conversion-events/smart-conversions`
				}
			]
		},
		{
			id: "ai",
			label: "IA",
			icon: icons.sparkles,
			mainRoute: ROUTE_AI,
			items: [
				...(!meetingAnalyzerPermission.mustBlock ? [{
					label: "Analisador de reunião",
					icon: icons.meeting,
					to: "/admin/meeting-analyzer",
					featureType: "new" as FeatureType
				}] : []),
				{
					label: "Playground",
					icon: icons.magicWand,
					to: hasPermissionToUseAIPlayground
						? playgroundIAUrl.cpURL
						: `${ROUTE_AI}/playground`,
					featureType: hasPermissionToUseAIPlayground
						? undefined
						: ("upcoming" as FeatureType),
					isExternal: hasPermissionToUseAIPlayground
				},
				...(hiddenTabs
					? [
						{
							label: "Agentes de IA",
							icon: icons.sparkles,
							to: `${ROUTE_AI}/agent`,
							featureType: "new" as FeatureType
						}
					]
					: [])
			]
		},
		{
			id: "reports",
			label: "Relatórios",
			icon: icons.report,
			mainRoute: ROUTE_REPORT,
			items: [
				{
					label: "Relatório de Tags",
					icon: icons.tagReport,
					to: `${ROUTE_REPORT}/tags-report`
				},
				{
					label: "Relatórios de UTM's",
					icon: icons.utmReport,
					to: `${ROUTE_REPORT}/utms-report`
				},
				{
					label: "Relatório de Ligações",
					icon: icons.voiceCallReport,
					to: `${ROUTE_REPORT}/voice-call-report`,
					featureType: "new"
				},
				{
					label: "Métricas avançadas",
					icon: icons.calculator,
					to: adminConsoleAppUrl.cpURL,
					isExternal: true,
					isDisabled: !canAccessAdminConsole,
					disabledTooltip: PERMISSION_DENIED_MESSAGE
				}
			]
		},
		{
			id: "plugins",
			label: "Plugins",
			icon: icons.plugin,
			mainRoute: ROUTE_PLUGINS,
			items: [
				{
					label: "Meta",
					icon: icons.meta,
					to: canUseConversionEvents
						? "/admin/plugins/meta-ads"
						: "/admin/smart-conversions"
				},
				{
					label: "Active Campaign",
					icon: icons.activeCampaign,
					to: `${ROUTE_PLUGINS}/active-campaign`
				},
				{
					label: "RD Station",
					icon: icons.rdStation,
					to: `${ROUTE_PLUGINS}/rd-station`
				},
				...(canUseSuperlogicaBlock ? [
					{
						label: "Superlógica",
						icon: icons.superlogica,
						to: `${ROUTE_PLUGINS}/superlogica`
					}
				] : []),
				...(globalStateStore.instance.subscriptionData?.limits?.permission?.agent ? [{
					label: "Google Drive",
					icon: icons.googleDrive,
					to: `${ROUTE_PLUGINS}/google-drive`
				}] : [])
			]
		},
		{
			id: "ecosystem",
			label: "Ecossistema",
			icon: icons.ecosystem,
			mainRoute: ROUTE_ECOSYSTEM,
			items: [
				{
					label: "Seja parceiro",
					icon: icons.handshake,
					to: `${ROUTE_ECOSYSTEM}/partnerships`
				},
				{
					label: "Indique e ganhe",
					icon: icons.gift,
					onClick: () => ReferralPopupTrigger.open()
				},
				{
					label: "Ferramentas gratuitas",
					icon: icons.tool,
					to: externalLinks.freeTools,
					isExternal: true
				},
				{
					label: "Academy",
					icon: icons.academy,
					to: externalLinks.academyMainPage,
					isExternal: true
				},
				{
					label: "Serviços para Clientes",
					icon: icons.service,
					to: externalLinks.servicesTable,
					isExternal: true
				},
				...(integrationType === "whatsapp"
					? [
						{
							label: "Migrar para API Oficial",
							icon: icons.whatsapp,
							onClick: () => WABAMigrationModal.open({
								initialName: globalStateStore.user.name ?? "",
								initialPhone: globalStateStore.instance.responsible_phone_number ?? ""
							}),
							isHighlighted: true
						}
					]
					: [])
			],
			placement: "right-end"
		}
	]

	const canAccessSubscriptions = checkPermission("manager-panel", "subscriptions-access")

	const accountItems: MenuItem[] = [
		{
			label: "Perfil",
			icon: icons.myData,
			to: `${ROUTE_ACCOUNT}/profile`
		},
		{
			label: "Pagamentos",
			icon: icons.creditCard,
			to: `${ROUTE_ACCOUNT}/signature`,
			isDisabled: !canAccessSubscriptions,
			disabledTooltip: PERMISSION_DENIED_MESSAGE
		},
		{
			label: "Admin Console",
			icon: icons.calculator,
			to: adminConsoleAppUrl.cpURL,
			isExternal: true,
			isDisabled: !canAccessAdminConsole,
			disabledTooltip: PERMISSION_DENIED_MESSAGE
		},
		{
			label: "Segurança",
			icon: icons.security,
			to: `${ROUTE_ACCOUNT}/security`
		},
		{
			label: "Sair",
			icon: icons.logout,
			onClick: handleLogout
		}
	]

	const bottomMenuCategories: MenuCategory[] = [
		{
			id: "help",
			label: "Preciso de ajuda",
			icon: icons.help,
			to: letalkLinks.wikiMainPage,
			isExternal: true,
			hasPopper: false,
			isSecondary: true
		},
		{
			id: "instance",
			label: `Instância ${globalStateStore.instance.instance_id}`,
			icon: icons.instance,
			items: [
				{
					label: "Trocar de instância",
					icon: icons.instance,
					onClick: handleOpenMultipleInstancesDialogue
				}
			]
		},
		{
			id: "myaccount",
			label: "Minha conta",
			icon: icons.myData,
			items: accountItems,
			mainRoute: ROUTE_ACCOUNT,
			placement: "right-end"
		}
	]

	const handleEnter = (id: string) => setHoveredCategory(id)
	const handleLeave = () => setHoveredCategory(null)
	const handleCategoryClick = (id: string) => {
		if (isSmallScreen) {
			setTimeout(() => {
				setHoveredCategory(hoveredCategory === id ? null : id)
			}, 0)
		}
	}

	return {
		defaultMenuCategories,
		bottomMenuCategories,
		hoveredCategory,
		setHoveredCategory,
		anchorRefs,
		handleEnter,
		handleLeave,
		handleCategoryClick,
		isInstanceOwner,
		canUseTrackableLinks,
		canUseConversionEvents
	}
}
